/**
 * qudaohao 编辑器：targeting_id=JSON 数组 `[{"渠道号":"留资ID"}, ...]`，ext_info 为空。
 *
 * 默认紧凑态：灰底 8 栏只读 chip 网格聚合展示 240 对（避免长下拉）；
 * 点「编辑」展开为行式可编辑（渠道号 chip + 留资 input + 删除 + 「新渠道号/新留资ID」添加）。
 * 序列化回 JSON 数组。
 */
import { useState } from "react";
import type { EditorProps } from "../redlineConfig/types";
import { parseJsonExt, stringifyJsonExt } from "../../utils/redlineHelpers";

interface Pair {
  qudaohao: string;
  liuzhi: string;
}

export function QudaohaoEditor({ item, onChange }: EditorProps) {
  const raw = parseJsonExt<Record<string, string>[]>(item.targeting_id, []);
  const pairs: Pair[] = raw.map((d) => {
    const k = Object.keys(d)[0] ?? "";
    return { qudaohao: k, liuzhi: d[k] ?? "" };
  });

  const [editing, setEditing] = useState(false);
  const [qhDraft, setQhDraft] = useState("");
  const [lzDraft, setLzDraft] = useState("");

  function commit(next: Pair[]) {
    onChange({
      targeting_id: stringifyJsonExt(
        next.filter((p) => p.qudaohao).map((p) => ({ [p.qudaohao]: p.liuzhi })),
      ),
      ext_info: item.ext_info ?? "",
    });
  }

  function setLiuzhi(i: number, v: string) {
    commit(pairs.map((p, j) => (j === i ? { ...p, liuzhi: v } : p)));
  }

  function remove(i: number) {
    commit(pairs.filter((_, j) => j !== i));
  }

  function add() {
    const qh = qhDraft.trim();
    if (!qh || pairs.some((p) => p.qudaohao === qh)) {
      setQhDraft("");
      setLzDraft("");
      return;
    }
    commit([...pairs, { qudaohao: qh, liuzhi: lzDraft.trim() }]);
    setQhDraft("");
    setLzDraft("");
  }

  // —— 紧凑态：灰底 8 栏只读聚合 ——
  if (!editing) {
    return (
      <div>
        <div className="mb-2 flex items-center justify-between">
          <span className="text-[12px] text-text-muted">
            共 <span className="text-text-emphasis">{pairs.length}</span> 对渠道号-留资ID
          </span>
          <button
            type="button"
            className="leo-btn-ghost"
            onClick={() => setEditing(true)}
          >
            编辑
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2 rounded-lg bg-da-card p-3 sm:grid-cols-3 md:grid-cols-8">
          {pairs.map((p, i) => (
            <div key={`${p.qudaohao}-${i}`} className="flex items-center gap-1.5">
              <span className="leo-tag is-info">{p.qudaohao}</span>
              <span className="text-[11px] text-text-muted">{p.liuzhi}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // —— 编辑态：行式可编辑 ——
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-end">
        <button
          type="button"
          className="leo-btn-ghost"
          onClick={() => setEditing(false)}
        >
          收起
        </button>
      </div>
      {pairs.map((p, i) => (
        <div key={`${p.qudaohao}-${i}`} className="rl-subbox flex flex-wrap items-center gap-2">
          <span className="leo-tag is-info">
            {p.qudaohao}
            <button
              type="button"
              className="ml-1 text-text-muted hover:text-text-emphasis"
              onClick={() => remove(i)}
              aria-label={`移除 ${p.qudaohao}`}
            >
              ×
            </button>
          </span>
          <input
            type="text"
            className="leo-input min-w-[14rem] flex-1"
            value={p.liuzhi}
            onChange={(e) => setLiuzhi(i, e.target.value)}
            placeholder="留资ID（与渠道号对应）"
          />
        </div>
      ))}
      <div className="flex flex-nowrap items-center gap-2 border-t border-line-subtle pt-2">
        <input
          type="text"
          className="leo-input min-w-0 flex-[1]"
          placeholder="新渠道号"
          value={qhDraft}
          onChange={(e) => setQhDraft(e.target.value)}
        />
        <input
          type="text"
          className="leo-input min-w-0 flex-[2]"
          placeholder="新留资ID"
          value={lzDraft}
          onChange={(e) => setLzDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
        />
        <button type="button" className="leo-btn-ghost shrink-0" onClick={add}>
          添加
        </button>
      </div>
    </div>
  );
}
